import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { studyQuestions, studyProfiles, mbtiProfiles } from '@/mocks/studyStyleData';
import { useTestResult } from '@/hooks/useTestResult';
import SaveStatusBanner from '@/components/feature/SaveStatusBanner';
import TestResumeBanner from '@/components/feature/TestResumeBanner';
import { TEST_PROGRESS_KEYS, readProgress, writeProgress, clearProgress } from '@/lib/testProgress';

type Step = 'intro' | 'test' | 'result';

const VARK_KEYS = ['V', 'A', 'R', 'K'];
const MBTI_KEYS = ['E', 'I', 'S', 'N', 'T', 'F', 'J', 'P'];

export default function StudyStylePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('intro');
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [activeSubject, setActiveSubject] = useState('국어');
  const [resumeDetail, setResumeDetail] = useState<string | null>(null);
  const advancingRef = useRef(false);

  const total = studyQuestions.length;
  const q = studyQuestions[current];

  // intro 진입 시 저장된 진행 상황 확인
  useEffect(() => {
    if (step !== 'intro') return;
    const saved = readProgress<{ current: number; answers: string[] }>(TEST_PROGRESS_KEYS.studyStyle);
    if (saved && (saved.answers.length > 0 || saved.current > 0)) {
      const done = Math.min(saved.current + 1, total);
      setResumeDetail(`성격·성향 기반 공부법 검사 ${done} / ${total}문항 진행 중`);
    } else {
      setResumeDetail(null);
    }
  }, [step, total]);

  // 검사 진행 상황 자동 저장
  useEffect(() => {
    if (step !== 'test') return;
    if (answers.length === 0 && current === 0) return;
    writeProgress(TEST_PROGRESS_KEYS.studyStyle, { current, answers });
  }, [step, current, answers]);

  const computeVarkScores = () => {
    const scores: Record<string, number> = { V: 0, A: 0, R: 0, K: 0 };
    answers.forEach(a => {
      if (VARK_KEYS.includes(a)) scores[a]++;
    });
    return scores;
  };

  const computeVark = () => {
    return Object.entries(computeVarkScores()).sort((a, b) => b[1] - a[1])[0][0];
  };

  const computeMbtiScores = () => {
    const scores: Record<string, number> = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };
    answers.forEach(a => {
      if (MBTI_KEYS.includes(a)) scores[a]++;
    });
    return scores;
  };

  const computeMbtiType = (scores: Record<string, number>) => {
    return (
      (scores.E >= scores.I ? 'E' : 'I') +
      (scores.S >= scores.N ? 'S' : 'N') +
      (scores.T >= scores.F ? 'T' : 'F') +
      (scores.J >= scores.P ? 'J' : 'P')
    );
  };

  const handleSelect = (value: string) => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setSelected(value);
    const newAnswers = [...answers];
    newAnswers[current] = value;
    setAnswers(newAnswers);
    setTimeout(() => {
      advancingRef.current = false;
      if (current + 1 < total) {
        setCurrent(current + 1);
        setSelected(newAnswers[current + 1] ?? null);
      } else {
        setStep('result');
      }
    }, 200);
  };

  const handlePrev = () => {
    if (current > 0) {
      setCurrent(current - 1);
      setSelected(answers[current - 1] ?? null);
    }
  };

  const clearProgressAll = () => {
    clearProgress(TEST_PROGRESS_KEYS.studyStyle);
    setResumeDetail(null);
  };

  const resume = () => {
    const saved = readProgress<{ current: number; answers: string[] }>(TEST_PROGRESS_KEYS.studyStyle);
    if (saved) {
      setAnswers(saved.answers || []);
      setCurrent(saved.current || 0);
      setSelected((saved.answers && saved.answers[saved.current]) ?? null);
      setStep('test');
    }
  };

  const topType = step === 'result' ? computeVark() : null;
  const varkScores = step === 'result' ? computeVarkScores() : null;
  const profile = topType ? studyProfiles[topType] : null;
  const subjects = profile ? Object.keys(profile.studyTips) : [];

  const mbtiScores = step === 'result' ? computeMbtiScores() : null;
  const mbtiType = mbtiScores ? computeMbtiType(mbtiScores) : null;
  const mbtiProfile = mbtiType ? mbtiProfiles[mbtiType] : null;

  const { saveTestResult, saveStatus } = useTestResult();
  useEffect(() => {
    if (step === 'result' && topType && mbtiType && profile) {
      saveTestResult('study_style', '성격·성향 기반 공부법 검사', null, {
        vark: topType,
        mbti: mbtiType,
      });
    }
    if (step === 'result') {
      clearProgressAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const mbtiDimensions = mbtiScores
    ? [
        { left: 'E', right: 'I', leftLabel: '외향', rightLabel: '내향' },
        { left: 'S', right: 'N', leftLabel: '감각', rightLabel: '직관' },
        { left: 'T', right: 'F', leftLabel: '사고', rightLabel: '감정' },
        { left: 'J', right: 'P', leftLabel: '판단', rightLabel: '인식' },
      ]
    : [];

  const varkColors: Record<string, string> = {
    V: 'primary', A: 'accent', R: 'secondary', K: 'primary'
  };

  const varkBg: Record<string, string> = {
    V: 'bg-primary-500', A: 'bg-accent-500', R: 'bg-secondary-500', K: 'bg-primary-700'
  };

  const varkMeta: Record<string, { label: string; icon: string; bar: string }> = {
    V: { label: '시각형', icon: 'ri-eye-line', bar: 'bg-primary-500' },
    A: { label: '청각형', icon: 'ri-headphone-line', bar: 'bg-accent-500' },
    R: { label: '읽기형', icon: 'ri-file-text-line', bar: 'bg-secondary-500' },
    K: { label: '체험형', icon: 'ri-hand-heart-line', bar: 'bg-primary-700' }
  };

  const renderMbtiBar = (left: string, right: string) => {
    if (!mbtiScores) return null;
    const leftScore = mbtiScores[left] || 0;
    const rightScore = mbtiScores[right] || 0;
    const sum = leftScore + rightScore || 1;
    const leftPct = Math.round((leftScore / sum) * 100);
    return (
      <div className="w-full bg-background-200 rounded-full h-2.5 overflow-hidden flex">
        <div className="bg-primary-500 h-full" style={{ width: `${leftPct}%` }}></div>
        <div className="bg-accent-500 h-full" style={{ width: `${100 - leftPct}%` }}></div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background-50">
      <nav className="bg-background-50 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => step === 'intro' ? navigate('/') : setStep('intro')}
            className="w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-primary-700 rounded-lg">
              <i className="ri-brain-line text-white text-xs"></i>
            </div>
            <span className="font-semibold text-foreground-950 text-sm">성격·성향 기반 공부법 검사</span>
          </div>
          {step === 'test' && (
            <div className="ml-auto flex items-center gap-2">
              <div className="w-24 sm:w-36 bg-background-200 rounded-full h-1.5 overflow-hidden">
                <div className="bg-primary-700 h-1.5 rounded-full transition-all" style={{ width: `${((current) / total) * 100}%` }}></div>
              </div>
              <span className="text-xs text-foreground-500 whitespace-nowrap">{current + 1} / {total}</span>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 py-6 md:py-10">
        {/* INTRO */}
        {step === 'intro' && (
          <div>
            <div className="bg-primary-700 rounded-2xl p-7 md:p-9 text-white text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-4">
                <i className="ri-brain-line text-3xl text-white"></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">성격·성향 기반 공부법 검사</h1>
              <p className="text-primary-200 text-sm md:text-base">
                VARK 학습 유형과 MBTI 성격 유형을 함께 분석하여<br />나에게 꼭 맞는 공부 전략을 제안합니다
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                <span className="text-xs bg-white/20 border border-white/30 px-3 py-1 rounded-full">50문항</span>
                <span className="text-xs bg-white/20 border border-white/30 px-3 py-1 rounded-full">VARK 학습 유형</span>
                <span className="text-xs bg-white/20 border border-white/30 px-3 py-1 rounded-full">MBTI 16유형</span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { type: 'V', name: '시각형', icon: 'ri-eye-line', desc: '도표·마인드맵으로 이해' },
                { type: 'A', name: '청각형', icon: 'ri-headphone-line', desc: '강의·소리내어 읽기' },
                { type: 'R', name: '읽기형', icon: 'ri-file-text-line', desc: '텍스트·필기로 정리' },
                { type: 'K', name: '체험형', icon: 'ri-hand-heart-line', desc: '직접 풀고 체험하기' },
              ].map((t) => (
                <div key={t.type} className="bg-background-50 border border-background-200 rounded-xl p-3 text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg mx-auto mb-2">
                    <i className={`${t.icon} text-primary-600`}></i>
                  </div>
                  <div className="font-semibold text-foreground-950 text-xs">{t.type} - {t.name}</div>
                  <div className="text-xs text-foreground-500 mt-0.5">{t.desc}</div>
                </div>
              ))}
            </div>

            <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-6">
              <h3 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-gift-line text-accent-500"></i>검사 후 받을 수 있는 것
              </h3>
              <div className="space-y-2">
                {[
                  '나의 VARK 학습 유형 분석',
                  '나의 MBTI 성격 유형 분석 (16유형)',
                  '국어·수학·영어·과학·사회 과목별 맞춤 공부법',
                  '성격 유형에 맞는 최적 공부 전략과 환경 가이드'
                ].map(item => (
                  <div key={item} className="flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-check-line text-primary-500 flex-shrink-0"></i>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {resumeDetail && (
              <div className="mb-4">
                <TestResumeBanner
                  detail={resumeDetail}
                  onResume={resume}
                  onReset={clearProgressAll}
                />
              </div>
            )}
            <button
              onClick={() => {
                clearProgressAll();
                setAnswers([]);
                setCurrent(0);
                setSelected(null);
                setStep('test');
              }}
              className="whitespace-nowrap w-full bg-primary-700 hover:bg-primary-800 text-white font-bold py-4 rounded-xl cursor-pointer transition-colors"
            >
              공부법 검사 시작하기 <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        )}

        {/* TEST */}
        {step === 'test' && (
          <div>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-primary-700">
                  {current < 26 ? 'VARK 학습 유형 검사' : 'MBTI 성격 유형 검사'}
                </span>
                <span className="text-sm text-foreground-500">{current + 1} / {total}</span>
              </div>
              <div className="w-full bg-background-200 rounded-full h-2">
                <div className="bg-primary-700 h-2 rounded-full transition-all" style={{ width: `${((current) / total) * 100}%` }}></div>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 mb-4">
              <div className="flex items-start gap-3 mb-6">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-700 rounded-lg flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">{current + 1}</span>
                </div>
                <h2 className="text-foreground-950 font-semibold text-base md:text-lg leading-relaxed">{q.question}</h2>
              </div>
              <div className="space-y-3">
                {q.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`whitespace-normal w-full text-left px-4 py-3.5 rounded-xl border text-sm transition-all cursor-pointer ${
                      selected === opt.value
                        ? 'border-primary-400 bg-primary-50 text-primary-800 font-medium'
                        : 'border-background-200 bg-background-50 text-foreground-700 hover:border-background-300 hover:bg-background-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selected === opt.value ? 'border-primary-500 bg-primary-500' : 'border-background-300'
                      }`}>
                        {selected === opt.value && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      {opt.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {current > 0 && (
                <button onClick={handlePrev} className="whitespace-nowrap px-6 py-3 rounded-xl border border-background-300 text-foreground-600 font-semibold text-sm hover:bg-background-100 cursor-pointer">
                  <i className="ri-arrow-left-line mr-2"></i>이전
                </button>
              )}
              <span className="text-xs text-foreground-400 ml-auto">답안을 선택하면 자동으로 다음으로 넘어갑니다</span>
            </div>
          </div>
        )}

        {/* RESULT */}
        {step === 'result' && profile && topType && mbtiProfile && mbtiType && (
          <div>
            {/* Result Badge */}
            <div className={`${varkBg[topType]} rounded-2xl p-6 md:p-8 text-white text-center mb-6`}>
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-4">
                <span className="text-3xl font-black">{topType}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                나는 <span className="underline decoration-2 underline-offset-4">{profile.name}</span>!
              </h1>
              <p className="text-white/80 text-sm md:text-base max-w-sm mx-auto">{profile.description}</p>
            </div>

            <SaveStatusBanner status={saveStatus} />

            {/* VARK 프로필 (4유형 분포) */}
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
              <h2 className="font-semibold text-foreground-950 mb-1 flex items-center gap-2">
                <i className="ri-eye-line text-primary-500"></i>
                내 학습 유형 프로필 (VARK)
              </h2>
              <p className="text-xs text-foreground-500 mb-4">
                네 가지 학습 유형의 점수 분포예요. 점수가 높을수록 해당 방식으로 배우는 것이 잘 맞아요.
              </p>
              <div className="space-y-3">
                {(['V', 'A', 'R', 'K'] as const).map((key) => {
                  const total = (varkScores!.V + varkScores!.A + varkScores!.R + varkScores!.K) || 1;
                  const pct = Math.round((varkScores![key] / total) * 100);
                  const meta = varkMeta[key];
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="flex items-center gap-1.5 text-sm text-foreground-700">
                          <i className={`${meta.icon} text-foreground-400`}></i>
                          <span className="font-semibold">{key}</span> · {meta.label}
                        </span>
                        <span className="text-sm font-semibold text-foreground-900">
                          {varkScores![key]} / {total} ({pct}%)
                        </span>
                      </div>
                      <div className="w-full bg-background-200 rounded-full h-2.5 overflow-hidden">
                        <div className={`${meta.bar} h-2.5 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* MBTI Type */}
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
              <h2 className="font-semibold text-foreground-950 mb-1 flex items-center gap-2">
                <i className="ri-user-heart-line text-accent-500"></i>
                나의 MBTI 성격 유형
              </h2>
              <div className="flex items-center gap-3 mt-3 mb-3">
                <div className="w-14 h-14 flex items-center justify-center bg-accent-500 text-white rounded-xl flex-shrink-0">
                  <span className="font-black text-lg tracking-wide">{mbtiType}</span>
                </div>
                <div>
                  <div className="font-bold text-foreground-950 text-lg">{mbtiType} · {mbtiProfile.name}</div>
                  <p className="text-foreground-600 text-sm mt-0.5">{mbtiProfile.description}</p>
                </div>
              </div>

              {/* 4 dimension bars */}
              <div className="space-y-3 mt-4">
                {mbtiDimensions.map((dim) => (
                  <div key={dim.left}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-primary-700 whitespace-nowrap">{dim.left} ({dim.leftLabel})</span>
                      <span className="text-foreground-500 whitespace-nowrap">{dim.rightLabel} ({dim.right})</span>
                    </div>
                    {renderMbtiBar(dim.left, dim.right)}
                  </div>
                ))}
              </div>
            </div>

            {/* MBTI Study Style */}
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
              <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-flash-line text-accent-500"></i>
                {mbtiType} 유형의 공부 전략
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div className="bg-accent-50 border border-accent-200 rounded-xl p-4">
                  <div className="text-xs font-semibold text-accent-700 mb-2">학습 강점</div>
                  <div className="flex flex-wrap gap-1.5">
                    {mbtiProfile.studyStrengths.map(s => (
                      <span key={s} className="text-xs bg-white text-accent-800 border border-accent-200 px-2 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
                <div className="bg-background-100 border border-background-200 rounded-xl p-4">
                  <div className="text-xs font-semibold text-foreground-600 mb-2">보완할 점</div>
                  <div className="flex flex-wrap gap-1.5">
                    {mbtiProfile.studyWeaknesses.map(w => (
                      <span key={w} className="text-xs bg-white text-foreground-600 border border-background-200 px-2 py-1 rounded-full">{w}</span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-foreground-600 text-sm leading-relaxed">{mbtiProfile.studyStyle}</p>
            </div>

            {/* VARK Strengths */}
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
              <h2 className="font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-star-line text-accent-500"></i>내 학습 강점 (VARK)
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.strengths.map(s => (
                  <span key={s} className="text-sm bg-accent-50 text-accent-700 border border-accent-200 px-3 py-1.5 rounded-full">{s}</span>
                ))}
              </div>
            </div>

            {/* Subject Tips */}
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
              <h2 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-book-open-line text-primary-500"></i>
                과목별 맞춤 공부법
              </h2>
              {/* Subject Tabs */}
              <div className="flex flex-wrap gap-2 mb-4">
                {subjects.map(subj => (
                  <button
                    key={subj}
                    onClick={() => setActiveSubject(subj)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                      activeSubject === subj
                        ? `${varkBg[topType]} text-white`
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {subj}
                  </button>
                ))}
              </div>
              {activeSubject && profile.studyTips[activeSubject] && (
                <div className="space-y-2.5">
                  {profile.studyTips[activeSubject].map((tip, i) => (
                    <div key={i} className="flex items-start gap-3 bg-background-100 rounded-xl p-3">
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${varkBg[topType]}`}>
                        <span className="text-white text-xs font-bold">{i + 1}</span>
                      </div>
                      <p className="text-foreground-700 text-sm leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Best Environment */}
            <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
              <h2 className="font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                <i className="ri-home-4-line text-secondary-500"></i>최적 공부 환경
              </h2>
              <p className="text-foreground-600 text-sm">{profile.bestEnvironment}</p>
            </div>

            {/* Worst Habits */}
            <div className="bg-background-50 border border-red-200 rounded-xl p-5 mb-4">
              <h2 className="font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                <i className="ri-close-circle-line text-red-400"></i>피해야 할 나쁜 습관
              </h2>
              <div className="space-y-1.5">
                {profile.worstHabits.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground-600">
                    <i className="ri-close-line text-red-400 flex-shrink-0"></i>
                    {h}
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendation */}
            <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 mb-6">
              <h2 className="font-semibold text-primary-800 mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-flash-line"></i>학종 활용 팁
              </h2>
              <p className="text-primary-700 text-sm leading-relaxed">{profile.recommendation}</p>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => { clearProgressAll(); setStep('intro'); setCurrent(0); setAnswers([]); setSelected(null); }} className="whitespace-nowrap flex-1 py-3.5 rounded-xl border border-background-300 text-foreground-600 font-semibold text-sm hover:bg-background-100 cursor-pointer">
                <i className="ri-refresh-line mr-2"></i>다시 검사
              </button>
              <button onClick={() => navigate('/career-test')} className="whitespace-nowrap flex-1 py-3.5 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 cursor-pointer">
                진로 탐색 이어서 하기 <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}